import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Exploring Two-Variable Data",
  examWeight: "5–7%",
  focus: "Variation, Patterns and Uncertainty, Data-Based Predictions",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Introducing Statistics: Are Variables Related?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Bivariate Data</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Unit 1 focused on univariate data (one variable). Unit 2 focuses on <strong>bivariate data</strong> (two variables) to determine if a relationship exists between them.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Explanatory vs. Response Variables</h3>
            <p className="text-slate-700 leading-relaxed mb-4">To study relationships, we must distinguish between the roles variables play.</p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Response Variable (Dependent / y)" 
                definition="Measures an outcome of a study."
              />
              <KeyTerm 
                term="Explanatory Variable (Independent / x)" 
                definition="May help explain or predict changes in a response variable."
              />
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900 mb-2">Example</h4>
              <p className="text-sm text-slate-700">In a study determining if study time affects test scores:</p>
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                <li><em>Explanatory:</em> Hours studied</li>
                <li><em>Response:</em> Test score</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Association vs. Causation</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Association:</strong> Exists if knowing the value of one variable helps predict the value of the other.</li>
              <li><strong>Causation:</strong> A change in the explanatory variable <em>causes</em> a change in the response variable.</li>
            </ul>
            <ImportantNote>
              <strong>Crucial Rule:</strong> Association does <strong>not</strong> imply causation. Causation can usually only be established through a well-designed controlled experiment (Unit 3).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Representing Two Categorical Variables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Contingency Tables (Two-Way Tables)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">A table that organizes data from two categorical variables.</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Rows:</strong> Typically represent the explanatory variable.</li>
              <li><strong>Columns:</strong> Typically represent the response variable.</li>
              <li><strong>Cells:</strong> Show the frequency (count) of observations that fit both categories.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Graphical Displays</h3>
            <ul className="list-disc pl-6 space-y-3 text-slate-700">
              <li><strong>Side-by-Side Bar Chart:</strong> Bars for different categories are placed next to each other for easy comparison.</li>
              <li><strong>Segmented (Stacked) Bar Chart:</strong> Each bar represents 100% of a group, divided into segments based on the second variable. Excellent for comparing <strong>proportions</strong>.</li>
              <li><strong>Mosaic Plot:</strong> Similar to a segmented bar chart, but the <em>width</em> of the bars varies based on the sample size of each group.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Statistics for Two Categorical Variables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Distributions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Using a two-way table, we can calculate three types of distributions:</p>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Marginal Distribution</h4>
                <p className="text-sm text-slate-700 mt-1">The distribution of <em>one</em> variable alone, ignoring the other. Found in the "margins" (totals) of the table.</p>
                <div className="mt-2 bg-white p-2 rounded">
                  <BlockMath math="\frac{\text{Row Total}}{\text{Table Total}} \quad \text{or} \quad \frac{\text{Column Total}}{\text{Table Total}}" />
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Joint Distribution</h4>
                <p className="text-sm text-slate-700 mt-1">The proportion of the total sample that fits into a specific cell (intersection of two categories).</p>
                <div className="mt-2 bg-white p-2 rounded">
                  <BlockMath math="\frac{\text{Cell Count}}{\text{Table Total}}" />
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Conditional Distribution</h4>
                <p className="text-sm text-slate-700 mt-1">The distribution of one variable <em>given</em> a specific condition (value) of the other variable. Critical for determining association.</p>
                <div className="mt-2 bg-white p-2 rounded">
                  <BlockMath math="\frac{\text{Cell Count}}{\text{Row Total}} \quad \text{or} \quad \frac{\text{Cell Count}}{\text{Column Total}}" />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Determining Association</h3>
            <p className="text-slate-700 leading-relaxed mb-4">To determine if two categorical variables are associated, compare their <strong>conditional distributions</strong>.</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Independence (No Association):</strong> If the conditional distributions are effectively the same across all groups.</li>
              <li><strong>Association:</strong> If the conditional distributions differ significantly.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Simpson's Paradox</h3>
            <KeyTerm 
              term="Simpson's Paradox" 
              definition="An association between two variables that holds for each individual group can reverse direction when the data are combined into a single group. This usually happens due to a lurking variable—a third variable that influences the relationship."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Representing the Relationship Between Two Quantitative Variables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Scatterplots</h3>
            <p className="text-slate-700 leading-relaxed mb-4">The standard graph for displaying the relationship between two quantitative variables.</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>X-axis:</strong> Explanatory variable.</li>
              <li><strong>Y-axis:</strong> Response variable.</li>
              <li><strong>Scaling:</strong> Both axes must be scaled uniformly.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Describing Scatterplots (DUFS)</h3>
            <Mnemonic title="DUFS" content="Direction, Unusual Features, Form, Strength — the four characteristics to address when describing a scatterplot." />
            <ol className="list-decimal pl-6 space-y-3 text-slate-700 mt-4">
              <li><strong>D - Direction:</strong> Positive (as <InlineMath math="x" /> increases, <InlineMath math="y" /> increases) or Negative (as <InlineMath math="x" /> increases, <InlineMath math="y" /> decreases).</li>
              <li><strong>U - Unusual Features:</strong> Identify outliers (points falling away from the pattern) or clusters.</li>
              <li><strong>F - Form:</strong> Is the pattern Linear or Non-linear (curved)?</li>
              <li><strong>S - Strength:</strong> How closely do the points follow the form? (Strong, Moderate, or Weak).</li>
            </ol>
            <ImportantNote>
              <strong>AP Exam Tip:</strong> Always include context. Instead of saying "It is a strong positive linear relationship," say "There is a strong, positive, linear relationship between <em>height</em> and <em>weight</em>."
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Correlation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Correlation Coefficient (<InlineMath math="r" />)</h3>
            <KeyTerm 
              term="Correlation Coefficient (r)" 
              definition="A numerical measure of the direction and strength of a linear relationship between two quantitative variables."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Properties of <InlineMath math="r" /></h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Range:</strong> <InlineMath math="-1 \le r \le 1" /></li>
              <li><strong>Interpretation:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li><InlineMath math="r \approx 0" />: Very weak to no linear relationship.</li>
                  <li><InlineMath math="r > 0" />: Positive relationship.</li>
                  <li><InlineMath math="r < 0" />: Negative relationship.</li>
                  <li><InlineMath math="r = 1" /> or <InlineMath math="-1" />: Perfect linear relationship.</li>
                </ul>
              </li>
              <li><strong>Linearity Constraint:</strong> <InlineMath math="r" /> only measures linear relationships. You can have a strong curved relationship with <InlineMath math="r \approx 0" />.</li>
              <li><strong>Unitless:</strong> Changing units (e.g., feet to meters) does <strong>not</strong> change <InlineMath math="r" />.</li>
              <li><strong>Non-Resistant:</strong> <InlineMath math="r" /> is strongly affected by outliers. A single outlier can drastically lower or raise correlation.</li>
              <li><strong>Symmetry:</strong> Correlation between <InlineMath math="x" /> and <InlineMath math="y" /> is the same as between <InlineMath math="y" /> and <InlineMath math="x" />.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Linear Regression Models",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Regression Line</h3>
            <p className="text-slate-700 leading-relaxed mb-4">A model used to predict the value of <InlineMath math="y" /> for a given value of <InlineMath math="x" />.</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-semibold text-slate-800 mb-2">Equation (AP Statistics notation)</h4>
              <BlockMath math="\hat{y} = a + bx" />
              <ul className="list-disc pl-5 mt-3 space-y-1 text-sm text-slate-700">
                <li><InlineMath math="\hat{y}" /> (y-hat): The <strong>predicted</strong> value of the response variable.</li>
                <li><InlineMath math="a" />: The y-intercept.</li>
                <li><InlineMath math="b" />: The slope.</li>
                <li><InlineMath math="x" />: The explanatory variable.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting Slope and Intercept</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Slope (<InlineMath math="b" />)</h4>
                <p className="text-sm text-slate-700 mt-2">"For every 1 unit increase in [explanatory variable], the predicted [response variable] changes by [slope value]."</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Y-Intercept (<InlineMath math="a" />)</h4>
                <p className="text-sm text-slate-700 mt-2">"When the [explanatory variable] is 0, the predicted [response variable] is [intercept value]."</p>
                <p className="text-sm text-amber-700 mt-2"><em>Note:</em> Interpret the intercept only if <InlineMath math="x=0" /> is possible in the context of the data.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Extrapolation</h3>
            <KeyTerm 
              term="Extrapolation" 
              definition="Using the regression line to make predictions for x-values far outside the range of the data used to build the model. These predictions are often unreliable."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Residuals",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <p className="text-slate-700 leading-relaxed mb-4">A residual is the vertical distance between an actual data point and the regression line.</p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="\text{Residual} = y - \hat{y} \quad (\text{Actual} - \text{Predicted})" />
            </div>
            <Mnemonic title="AP" content="Actual minus Predicted — how to remember the residual formula." />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting Residuals</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Positive Residual:</strong> The actual value was higher than predicted (Model under-predicted).</li>
              <li><strong>Negative Residual:</strong> The actual value was lower than predicted (Model over-predicted).</li>
              <li><strong>Zero Residual:</strong> The point lies exactly on the line.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Residual Plots</h3>
            <p className="text-slate-700 leading-relaxed mb-4">A scatterplot of the residuals (<InlineMath math="y" />-axis) against the explanatory variable (<InlineMath math="x" />-axis).</p>
            <KeyTerm term="Purpose" definition="To check if a linear model is appropriate." />
            <div className="mt-4 space-y-3">
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-sm"><strong>Random Scatter (No pattern):</strong> The linear model <strong>is</strong> appropriate.</p>
              </div>
              <div className="bg-red-50 p-3 rounded border border-red-200">
                <p className="text-sm"><strong>Curved Pattern (U-shape or arch):</strong> The linear model is <strong>not</strong> appropriate (original data is non-linear).</p>
              </div>
              <div className="bg-amber-50 p-3 rounded border border-amber-200">
                <p className="text-sm"><strong>Fanning (Cone shape):</strong> The linear model may not be appropriate due to inconsistent variance (heteroscedasticity).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Least Squares Regression",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Least Squares Regression Line (LSRL)</h3>
            <KeyTerm 
              term="LSRL" 
              definition="The 'line of best fit' is the line that minimizes the sum of the squared residuals."
            />
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
              <BlockMath math="\text{Minimize } \sum \text{residual}^2" />
            </div>
            <p className="text-slate-700 mt-4">The line always passes through the point of averages <InlineMath math="(\bar{x}, \bar{y})" />.</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculating the Line</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Given summary statistics, you can calculate the slope and intercept:</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900 mb-2">Slope</h4>
                <BlockMath math="b = r \left( \frac{s_y}{s_x} \right)" />
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><InlineMath math="r" />: Correlation coefficient</li>
                  <li><InlineMath math="s_y" />: Standard deviation of <InlineMath math="y" /></li>
                  <li><InlineMath math="s_x" />: Standard deviation of <InlineMath math="x" /></li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900 mb-2">Intercept</h4>
                <BlockMath math="a = \bar{y} - b\bar{x}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Coefficient of Determination (<InlineMath math="r^2" />)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Describes the fraction of variation in the response variable explained by the model.</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Calculation:</strong> Square the correlation coefficient <InlineMath math="r" />.</li>
              <li><strong>Interpretation Template:</strong> "About [<InlineMath math="r^2" /> as %] of the variation in [response variable] is accounted for by the linear model relating it to [explanatory variable]."</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Reading Computer Output</h3>
            <p className="text-slate-700 leading-relaxed mb-4">AP Exams often provide Minitab or JMP output. You must locate the coefficients:</p>
            <div className="bg-slate-100 p-4 rounded-lg border border-slate-300">
              <p className="font-mono text-sm"><strong>Coef / Estimate Column:</strong></p>
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                <li>Row 1 (Constant/Intercept): This is <InlineMath math="a" /> (y-intercept).</li>
                <li>Row 2 (Variable Name): This is <InlineMath math="b" /> (slope).</li>
              </ul>
              <p className="mt-3 text-sm"><strong>s:</strong> The standard deviation of the residuals (typical prediction error).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: Analyzing Departures from Linearity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Outliers and Influential Points</h3>
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Outlier</h4>
                <p className="text-sm text-slate-700 mt-1">An observation that lies outside the overall pattern.</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><em>In Y-direction:</em> Has a large residual.</li>
                  <li><em>In X-direction:</em> Has high leverage (far from the mean of x).</li>
                </ul>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Influential Point</h4>
                <p className="text-sm text-slate-700 mt-1">A point that, if removed, substantially changes the slope, y-intercept, or correlation.</p>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li>Points with high leverage (extreme x-values) are often influential.</li>
                  <li>A point can have high leverage but small residual if it falls in line with the pattern; this point strengthens the correlation but may not change the slope much.</li>
                  <li>If a high leverage point is <em>off</em> the line, it tilts the line significantly.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Transformations to Achieve Linearity</h3>
            <p className="text-slate-700 leading-relaxed mb-4">If a residual plot shows a curve, we can transform the data to make it linear.</p>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">Power Model (<InlineMath math="y = ax^p" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Try plotting <InlineMath math="\log(y)" /> vs. <InlineMath math="\log(x)" />.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Exponential Model (<InlineMath math="y = ab^x" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Try plotting <InlineMath math="\log(y)" /> vs. <InlineMath math="x" />.</p>
              </div>
            </div>
            <p className="text-slate-700 mt-4">Once transformed data appears linear, you perform regression on the transformed variables.</p>
          </section>
        </div>
      )
    },
    {
      id: "unit-2-summary",
      title: "Summary of Key Formulas (Unit 2)",
      content: (
        <div className="space-y-6">
          <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
            <h3 className="text-xl font-bold text-slate-800 mb-4">Key Formulas</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700 w-48">Regression Line:</span>
                <InlineMath math="\hat{y} = a + bx" />
              </div>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700 w-48">Residual:</span>
                <InlineMath math="y - \hat{y}" />
              </div>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700 w-48">Slope from Statistics:</span>
                <InlineMath math="b = r \frac{s_y}{s_x}" />
              </div>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700 w-48">Y-intercept:</span>
                <InlineMath math="a = \bar{y} - b\bar{x}" />
              </div>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700 w-48">Correlation:</span>
                <span className="text-slate-700">Measures linear strength and direction</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-semibold text-slate-700 w-48">Coefficient of Determination:</span>
                <InlineMath math="r^2" /> <span className="text-slate-600 ml-2">— Percent of variation explained</span>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ]
};

export default statisticsUnit2Notes;
